import { Button, Card, DatePicker, Form, message, Row, Select, Table, Tooltip } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { fetchEmployeePerformenceAssessment } from 'appRedux/actions';
import { StyledTable } from 'components/consultant/Table/StyledTableComponent/StyledConsultantFixedheaderTable';
import { active_InActiveOptions, assessmentStatusOptions, assessmentTypeOptions } from 'constants/Roles';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { displayDateInMMDDYYYY } from 'routes/recruitment/screening/utils';
import { MultipleSelectWithCount } from 'util/Filters/FilterUtils';
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons/lib/icons"
import ExcelJS from 'exceljs';

const AssessmentData = ({ user, dispatch }) => {

    const [form] = useForm()
    const { assessmentData } = useSelector(({ submissions }) => submissions)
    const [tableData, setTableData] = useState(null)
    const [employeeFilterOptions, setEmployeeFilterOptions] = useState([])
    const [currentFilter, setCurrentFilter] = useState({})
    const [startEndDate, setStartEndDate] = useState(null)

    useEffect(() => {
        if (!assessmentData) {
            const from_date = dayjs().startOf('month').format('YYYY-MM-DD');
            const to_date = dayjs().format('YYYY-MM-DD');
            setStartEndDate({ from_date, to_date })
            setCurrentFilter({ from_date, to_date })
            dispatch(fetchEmployeePerformenceAssessment({ from_date: from_date, to_date: to_date }))
        }
        form.setFieldValue('from_date', dayjs().startOf('months'))
        form.setFieldValue('to_date', dayjs())
    }, [])

    const formattedData = useMemo(() => {
        if (!assessmentData) return [];

        const newData = assessmentData.flatMap(user => {
            if (user.assessments.length > 0) {
                return user.assessments.map((assessment, index) => ({
                    ...assessment,
                    employee: user.full_name,
                    employee_id: user.id,
                }));
            } else {
                return [{
                    employee: user.full_name,
                    employee_id: user.id,
                    consultant: null,
                    interview_start_time: null,
                    interview_end_time: null,
                    assessment_type: null,
                    level: null,
                    job_title: null,
                    technologies: null,
                    status: null,
                }];
            }
        });
        const filterOpt = newData?.map((employeeItem) => ({
            id: employeeItem.employee_id,
            name: employeeItem.employee,
        }));
        // Use a Map to ensure unique items by 'id'
        const uniqOpt = new Map();

        filterOpt.forEach((newItem) => {
            // Only add if the 'id' doesn't already exist in the map
            if (!uniqOpt.has(newItem.id)) {
                uniqOpt.set(newItem.id, newItem);
            }
        });
        const uniqueFilterOptions = Array.from(uniqOpt.values()); // Convert the Map values to an array
        setEmployeeFilterOptions(uniqueFilterOptions);

        setTableData(newData)
        return newData;
    }, [assessmentData]);

    const handleRemoveAll = () => {
        setTableData(formattedData)
        form.resetFields(['assessment_type', 'employee', 'status', 'level', 'is_active'])
        dispatch(fetchEmployeePerformenceAssessment({ from_date: startEndDate?.from_date, to_date: startEndDate?.to_date }))
    }

    const handleFilterChange = (values) => {
        if (values) {
            let filteredData = formattedData;
            if (values.employee && values.employee.length > 0) {
                filteredData = filteredData.filter(item => values.employee.includes(item.employee_id));
            }
            setTableData(filteredData);
        }
    };

    const handleSubmitFilters = (values) => {
        if (!values) return

        Object.keys(values).forEach(key => {
            if (values[key] === "" || values[key] === undefined || values[key] === null || values[key] === 'null' ||
                (Array.isArray(values[key]) && values[key].length === 0)) {
                delete values[key];
            }
            if (['assessment_type', 'status', 'level'].includes(key) && Array.isArray(values[key])) {
                values[key] = values[key].join(',');
            }
            if (['from_date', 'to_date'].includes(key) && values[key]) {
                values[key] = dayjs(values[key]).format('YYYY-MM-DD')
            }
        });

        const filterData = {
            assessment_type: values.assessment_type ? values.assessment_type : null,
            status: values.status ? values.status : null,
            level: values.level ? values.level : null,
            from_date: values.from_date ? values.from_date : null,
            to_date: values.to_date ? values.to_date : null,
            is_active: values.is_active ? values.is_active : null,
        }
        dispatch(fetchEmployeePerformenceAssessment({ ...filterData }))
        setCurrentFilter(filterData)
    }

    const handleDateChange = (date, filterType) => {
        const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
        const from_date = filterType === 'from_date' ? formattedDate : dayjs(form.getFieldValue('from_date')).format('YYYY-MM-DD');
        const to_date = filterType === 'to_date' ? formattedDate : dayjs(form.getFieldValue('to_date')).format('YYYY-MM-DD');
        setStartEndDate({ from_date, to_date })
    };

    const column = [
        {
            title: 'Consultant',
            dataIndex: 'consultant',
            key: 'consultant',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Employee',
            dataIndex: 'employee',
            key: 'employee',
            align: 'center',
            // sorter: (a, b) => (a.employee || '').toLowerCase().trim().localeCompare((b.employee || '').toLowerCase().trim()),
            filterDropdown: <div style={{ padding: "15px 24px 0px 22px" }}>
                <MultipleSelectWithCount
                    name="employee"
                    placeholder="employee"
                    options={employeeFilterOptions?.map(i => ({ value: i.id, label: i.name }))}
                    width={200}
                    dropdownMatchSelectWidth={300}
                    onChange={handleFilterChange}
                />
            </div>,
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Vendor',
            dataIndex: 'vendor',
            key: 'vendor',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Assessment Type',
            dataIndex: 'assessment_type',
            key: 'assessment_type',
            align: 'center',
            // sorter: (a, b) => {
            //   const order = {
            //     Interview: 1,
            //     Screening: 2,
            //     Test: 3
            //   };
            //   const valA = order[a.assessment_type] ?? 999;
            //   const valB = order[b.assessment_type] ?? 999;
            //   return valA - valB;
            // },
            render: (text) => <span className="gx-badge">{text || 'N/A'}</span>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Job Title',
            dataIndex: 'job_title',
            key: 'job_title',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
        {
            title: 'Interview Start Time',
            dataIndex: 'interview_start_time',
            key: 'interview_start_time',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? displayDateInMMDDYYYY(text) : 'N/A'} </span>
        },
        {
            title: 'Interview End Time',
            dataIndex: 'interview_end_time',
            key: 'interview_end_time',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? displayDateInMMDDYYYY(text) : 'N/A'} </span>
        },
        {
            title: 'Technologies',
            dataIndex: 'technologies',
            key: 'technologies',
            align: 'center',
            render: (text, record) => <span className={'gx-badge'}> {text ? text : 'N/A'} </span>
        },
    ]

    const convertArrayOfObjectsToCSV = (data) => {
        if (data && data.length === 0) {
            return
        }
        const csv = [];
        // Header row
        csv.push(Object.keys(data[0]).join(','));

        // Data rows
        data.forEach((item) => {
            const values = Object.values(item);
            const row = values.map((value) => `"${value}"`).join(',');
            csv.push(row);
        });
        return csv.join('\n');
    }

    const handleDownloadData = () => {
        if (tableData && tableData.length > 0) {
            const groupedByEmployee = new Map();

            // Group tableData by employee_id
            tableData.forEach((item) => {
                if (!groupedByEmployee.has(item.employee_id)) {
                    groupedByEmployee.set(item.employee_id, {
                        employee: item.employee,
                        employee_id: item.employee_id,
                        values: [],
                    });
                }
                groupedByEmployee.get(item.employee_id).values.push(item);
            });

            // Create a new workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Assessments');

            // Styling for headers
            worksheet.columns = [
                { key: 'consultant', width: 20 },
                { key: 'employee', width: 20 },
                { key: 'vendor', width: 20 },
                { key: 'client', width: 20 },
                { key: 'interview_start_time', width: 20 },
                { key: 'assessment_type', width: 20 },
                { key: 'job_title', width: 20 },
                { key: 'technologies', width: 20 },
                { key: 'level', width: 10 },
            ];
            const employeeRow = worksheet.addRow([
                'Consultant',
                'Employee',
                'Vendor',
                'Client',
                'Date of assessment',
                'Assessment Type',
                'Job Title',
                'Technologies',
                'Level',
            ]);
            employeeRow.getCell(1).font = { bold: true, color: { argb: 'FF003366' } }; // Blue color
            employeeRow.eachCell((cell, colNumber) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF003366' }  // Dark Blue background
                };
                cell.font = {
                    bold: true,
                    color: { argb: 'FFFFFFFF' }    // White text
                };
                cell.alignment = {
                    horizontal: 'center',
                    vertical: 'middle'
                };
            });

            const entriesToDownload = Object.values(Object.fromEntries(groupedByEmployee.entries())).reverse() || [] // this is to download the sorted data in the xlsx file
            entriesToDownload.forEach((employeeDetails) => {
                const assessment_data = employeeDetails.values;

                // Add employee name with style
                // const employeeRow = worksheet.addRow([employeeDetails.employee.toUpperCase()]); // this is to shoe the data employee wise in the xlsx file
                // employeeRow.getCell(1).font = { bold: true, color: { argb: 'FF003366' } }; // Blue color
                // employeeRow.getCell(1).alignment = { horizontal: 'center' };
                // worksheet.mergeCells(`A${employeeRow.number}:F${employeeRow.number}`); // Merge all columns for this row

                // employeeRow.eachCell((cell, colNumber) => {
                //     cell.fill = {
                //       type: 'pattern',
                //       pattern: 'solid',
                //       fgColor: { argb: 'FF003366' }  // Dark Blue background
                //     };
                //     cell.font = {
                //       bold: true,
                //       color: { argb: 'FFFFFFFF' }    // White text
                //     };
                //     cell.alignment = {
                //       horizontal: 'center',
                //       vertical: 'middle'
                //     };
                //   });

                // Add header row for the data (if not already included)
                // worksheet.addRow([
                //     'Consultant',
                //     'Employee',
                //     'Date of assessment',
                //     'Assessment Type',
                //     'Job Title',
                //     'Technologies',
                //     'Level',
                // ]);

                // Add data rows for each assessment
                assessment_data.forEach((item) => {
                    worksheet.addRow([
                        item.consultant || 'N/A',
                        item.employee || 'N/A',
                        item.vendor || 'N/A',
                        item.client || 'N/A',
                        item.interview_start_time || 'N/A',
                        item.assessment_type || 'N/A',
                        item.job_title || 'N/A',
                        item.technologies || 'N/A',
                        item.level || 'N/A',
                    ]);
                });
                // worksheet.addRow([])
            });

            // Save the Excel file and download it
            workbook.xlsx.writeBuffer().then((buffer) => {
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'assessments_data.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
        } else {
            message.error('Nothing to download!');
        }
    };

    return (
        <div className='gx-module-box-content p-2'>
            <Form className="consultant-filter-input consultant-filter-container" form={form} onFinish={handleSubmitFilters}>
                <Row justify="start" style={{ marginBottom: "5px" }} >
                    <MultipleSelectWithCount
                        name="status"
                        placeholder="status"
                        options={assessmentStatusOptions.map(i => ({ value: i.value, label: i.name }))}
                    />
                    <MultipleSelectWithCount
                        name="assessment_type"
                        placeholder="assessment type"
                        options={assessmentTypeOptions.map(i => ({ value: i.value, label: i.name }))}
                    />
                    <MultipleSelectWithCount
                        name="level"
                        placeholder="level"
                        options={[{ value: 'l1', name: 'Round 1' }, { value: 'l2', name: 'Round 2' }, { value: 'f', name: 'Final Round' }]
                            .map(i => ({ value: i.value, label: i.name }))
                        }
                    />
                    <Form.Item name="is_active">
                        <Select
                            showSearch
                            allowClear
                            placeholder="is active"
                            optionFilterProp="children"
                            popupMatchSelectWidth={100}
                            filterOption={(input, option) =>
                                String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                            {active_InActiveOptions.map(i => (<Select.Option value={i.value}>{i.name}</Select.Option>))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="from_date">
                        <DatePicker
                            style={{ width: '100%', lineHeight: 1 }}
                            placeholder="from date"
                            format="YYYY-MM-DD"
                            allowClear={false}
                            disabledDate={(current) => {
                                const to_date = form.getFieldValue('to_date')
                                if (to_date && current) {
                                    return current.isAfter(to_date)
                                } else {
                                    return false
                                }
                            }}
                            onChange={(date) => handleDateChange(date, 'from_date')}
                        />
                    </Form.Item>
                    <Form.Item name="to_date">
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="to date"
                            format="YYYY-MM-DD"
                            allowClear={false}
                            disabledDate={(current) => {
                                const from_date = form.getFieldValue('from_date')
                                if (from_date && current) {
                                    return current.isBefore(from_date)
                                } else {
                                    return false
                                }
                            }}
                            onChange={(date) => handleDateChange(date, 'to_date')}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit"> Apply </Button>
                        <Tooltip title="Remove filter">
                            <img src={require("assets/images/delete image.png")} alt="Remove filter" onClick={handleRemoveAll} style={{ width: "20px", height: "20px", cursor: "pointer" }} />
                        </Tooltip>
                        <Tooltip title="Download Data">
                            <DownloadOutlined style={{ fontSize: '20px', cursor: 'pointer', marginLeft: 8 }} onClick={handleDownloadData} />
                        </Tooltip>
                        <Tooltip title="Start and End date are always required! They cannot be removed.">
                            <InfoCircleOutlined style={{ color: 'red', cursor: 'pointer', marginLeft: 8 }} />
                        </Tooltip>
                    </Form.Item>
                </Row>
            </Form>
            <StyledTable
                columns={column}
                dataSource={tableData}
                pagination={false}
                bordered
            // onChange={(pagination, filters, sorter) => {
            //     if (sorter.order) {
            //       const sortedData = [...tableData].sort((a, b) =>
            //         (a.employee || '').toLowerCase().trim().localeCompare((b.employee || '').toLowerCase().trim())
            //       );
            //       setTableData(sortedData);
            //     } else {
            //       setTableData(formattedData); // or refetch/reset if needed
            //     }
            // }}
            />
        </div>
    )
}

export default AssessmentData